/**
 * Shared selection behavior hook
 *
 * Provides core selection logic (click handling, additive selection)
 * with enable/disable support.
 */

import { useCallback, useMemo, type MouseEvent } from 'react'

export interface UseSelectOptions {
	blockId: string
	selectedIds: Set<string>
	onSelect: (blockId: string, additive: boolean) => void
	enabled?: boolean
}

export interface UseSelectReturn {
	isSelected: boolean
	handlers: {
		onClick: (e: MouseEvent) => void
	}
}

/**
 * Hook for managing selection state and behavior
 *
 * @example
 * ```tsx
 * const { isSelected, handlers } = useSelect({
 *   blockId: block.id,
 *   selectedIds: context.selectedIds,
 *   onSelect: context.onSelect,
 *   enabled: true
 * })
 *
 * <div onClick={handlers.onClick}>...</div>
 * ```
 */
export function useSelect({ blockId, selectedIds, onSelect, enabled = true }: UseSelectOptions): UseSelectReturn {
	const isSelected = useMemo(() => selectedIds.has(blockId), [selectedIds, blockId])

	const handleClick = useCallback(
		(e: MouseEvent) => {
			if (!enabled) return

			e.preventDefault()
			e.stopPropagation()

			// Shift key enables additive selection
			const isAdditive = e.shiftKey

			onSelect(blockId, isAdditive)
		},
		[enabled, blockId, onSelect]
	)

	return {
		isSelected,
		handlers: {
			onClick: handleClick,
		},
	}
}
